/**
 * Telefonszám validációs és formázó segédfüggvények.
 * Központi helyen kezeli a magyar mobil telefonszámok validálását és formázását.
 */

const VALID_MOBILE_PREFIXES = ['3620', '3630', '3631', '3650', '3670'];

/** Érvényes magyar mobilszám prefixek (a +36 utáni két számjegy). */
export const VALID_AREA_CODES = ['20', '30', '31', '50', '70'];

/**
 * Telefonszám validálása. Visszaadja, hogy érvényes-e.
 * null = üres/semleges, true = érvényes, false = érvénytelen.
 */
export function validatePhoneNumber(value: string): boolean | null {
  if (!value || value === '+36 ') {
    return null;
  }

  const cleanPhone = value.replace(/[\s\-]/g, '');

  if (!cleanPhone.startsWith('+36') || cleanPhone.length !== 12) {
    return false;
  }

  const prefix = cleanPhone.substring(1, 5);
  if (!VALID_MOBILE_PREFIXES.includes(prefix)) {
    return false;
  }

  const remainingDigits = cleanPhone.substring(5);
  return /^\d{7}$/.test(remainingDigits);
}

/**
 * Angular FormControl validátor telefonszámhoz.
 * Használható ReactiveFormsModule-lal (pl. SmallInputMenuComponent).
 */
export function phoneFormValidator(control: { value: string | null }): { invalid: true } | null {
  if (!control.value) return null;

  const value = control.value.trim();
  const cleanPhone = value.replace(/[\s\-]/g, '');

  if (!cleanPhone.startsWith('+36') || cleanPhone.length !== 12) {
    return { invalid: true };
  }

  const prefix = cleanPhone.substring(1, 5);
  if (!VALID_MOBILE_PREFIXES.includes(prefix)) {
    return { invalid: true };
  }

  const remainingDigits = cleanPhone.substring(5);
  if (!/^\d{7}$/.test(remainingDigits)) {
    return { invalid: true };
  }

  return null;
}

/**
 * Nyers telefonszám érték tisztítása és normalizálása.
 * Eltávolítja a nem-szám karaktereket, kezeli a prefixeket.
 */
export function cleanPhoneValue(rawValue: string): string {
  let value = rawValue.replace(/[^\d+]/g, '');

  value = value.replace(/\++/g, '+');
  if (value.indexOf('+') > 0) {
    value = value.replace(/\+/g, '');
    value = '+' + value;
  }

  if (value.startsWith('06')) {
    value = '+36' + value.substring(2);
  } else if (value.startsWith('36') && !value.startsWith('+36')) {
    value = '+' + value;
  } else if (value.match(/^\d/) && !value.startsWith('+')) {
    value = '+36' + value;
  }

  if (value.startsWith('+36') && value.substring(3).length > 9) {
    value = '+36' + value.substring(3, 12);
  }

  return value;
}

/**
 * Telefonszám formázása szóközökkel: +36 XX XXX XXXX
 */
export function formatPhoneNumber(cleanValue: string): string {
  if (!cleanValue.startsWith('+36')) return cleanValue;

  const digits = cleanValue.substring(3);
  if (digits.length === 0) return '+36 ';
  if (digits.length <= 2) return '+36 ' + digits;
  if (digits.length <= 5) return '+36 ' + digits.substring(0, 2) + ' ' + digits.substring(2);
  return '+36 ' + digits.substring(0, 2) + ' ' + digits.substring(2, 5) + ' ' + digits.substring(5);
}

/**
 * Kurzorpozíció kiszámítása a formázott szövegben.
 * A formázás előtti kurzornál lévő számjegyek száma alapján kalkulál.
 */
export function calculateCursorPosition(formatted: string, prevValue: string, prevCursorPos: number): number {
  const digitsBeforeCursor = prevValue.substring(0, prevCursorPos).replace(/[^\d]/g, '').length;

  let newCursorPos = formatted.length;
  let digitCount = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      digitCount++;
    }
    if (digitCount === digitsBeforeCursor) {
      newCursorPos = i + 1;
      break;
    }
  }
  if (digitCount < digitsBeforeCursor) {
    newCursorPos = formatted.length;
  }
  return newCursorPos;
}

/**
 * KeyboardEvent handler: csak számjegyeket enged, és max 9 számjegyet a +36 után.
 * Használat: (keydown)="blockExcessPhoneDigits($event)"
 */
export function blockExcessPhoneDigitsOnKeyDown(event: KeyboardEvent, currentPhoneValue: string): void {
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
  if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
    return;
  }

  if (!/\d/.test(event.key)) {
    event.preventDefault();
    return;
  }

  const cleanValue = currentPhoneValue.replace(/[^\d+]/g, '');
  if (cleanValue.startsWith('+36')) {
    const digitsAfterPrefix = cleanValue.substring(3);
    if (digitsAfterPrefix.length >= 9) {
      const input = event.target as HTMLInputElement;
      if (input.selectionStart === input.selectionEnd) {
        event.preventDefault();
      }
    }
  }
}
